import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Checkbox, IconButton } from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { uploadTempFileRequest } from "../../../../../utils/APICalls";

type Props = {
  formik: any;
  disabled?: boolean;
};

interface AttachmentFile {
  id: string;
  name: string;
  size: string;
  isUploaded: boolean;
  sendToInspector: boolean;
  includeWithResults: boolean;
  fileKey?: string;
  isUploading?: boolean;
  isExisting: boolean;
}

const Attachments = (props: Props) => {
  const { formik, disabled = false } = props;
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);

  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File }>(
    {}
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  useEffect(() => {
    if (
      formik.values.attachments &&
      formik.values.attachments.length > 0 &&
      attachments.length === 0
    ) {
      const mappedAttachments: AttachmentFile[] = formik.values.attachments.map(
        (att: any) => ({
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: att.attachmentName || "Unknown File",
          size: att.size || "Unknown",
          isUploaded: true,
          sendToInspector: att.sendToInspector || false,
          includeWithResults: att.includeWithResult || false,
          fileKey: att.attachmentKey,
          isExisting: true,
        })
      );
      setAttachments(mappedAttachments);
    }
  }, [formik.values.attachments, attachments.length]); // eslint-disable-line

  useEffect(() => {
    const existingAttachments = attachments
      .filter((att) => att.isExisting && att.fileKey)
      .map((att) => ({
        attachmentKey: att.fileKey,
        attachmentName: att.name,
        sendToInspector: att.sendToInspector,
        includeWithResult: att.includeWithResults,
      }));

    const newUploadedAttachments = attachments
      .filter((att) => !att.isExisting && att.isUploaded && att.fileKey)
      .map((att) => ({
        attachmentKey: att.fileKey,
        sendToInspector: att.sendToInspector,
        includeWithResult: att.includeWithResults,
      }));

    const currentExisting = formik.values.attachments || [];
    const currentNew = formik.values.newAttachments || [];

    const existingChanged =
      JSON.stringify(existingAttachments) !== JSON.stringify(currentExisting);
    const newChanged =
      JSON.stringify(newUploadedAttachments) !== JSON.stringify(currentNew);

    if (existingChanged) {
      formik.setFieldValue("attachments", existingAttachments);
    }
    if (newChanged) {
      formik.setFieldValue("newAttachments", newUploadedAttachments);
    }
  }, [attachments]); // eslint-disable-line

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadError(false);
      setUploadErrorMessage("");

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        setUploadError(true);
        setUploadErrorMessage(
          "Unsupported file type. Only PDF, DOC, DOCX, XLSX, JPG, or PNG files are allowed."
        );
        return;
      }

      if (file.size > 10000000) {
        setUploadError(true);
        setUploadErrorMessage("File size exceeds 10MB limit.");
        return;
      }

      const newAttachment: AttachmentFile = {
        id: Date.now().toString(),
        name: file.name,
        size: `${(file.size / 1024).toFixed(0)}kb`,
        isUploaded: false,
        sendToInspector: false,
        includeWithResults: false,
        isUploading: true,
        isExisting: false,
      };

      setAttachments((prev) => [...prev, newAttachment]);
      setSelectedFiles((prev) => ({ ...prev, [newAttachment.id]: file }));

      try {
        const response = await uploadTempFileRequest(file);

        if (response && response.data) {
          setUploadError(false);
          setUploadErrorMessage("");
          setAttachments((prev) =>
            prev.map((att) =>
              att.id === newAttachment.id
                ? {
                    ...att,
                    isUploaded: true,
                    fileKey: response.data,
                    isUploading: false,
                  }
                : att
            )
          );
        } else {
          setAttachments((prev) =>
            prev.filter((att) => att.id !== newAttachment.id)
          );
          setSelectedFiles((prev) => {
            const newFiles = { ...prev };
            delete newFiles[newAttachment.id];
            return newFiles;
          });
          setUploadError(true);
          setUploadErrorMessage(
            response?.data?.detail || "Failed to upload file. Please try again."
          );
        }
      } catch (error) {
        setAttachments((prev) =>
          prev.filter((att) => att.id !== newAttachment.id)
        );
        setSelectedFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[newAttachment.id];
          return newFiles;
        });
        setUploadError(true);
        setUploadErrorMessage("Failed to upload file. Please try again.");
      }
    }
  };

  const handleFileDelete = (attachmentId: string) => {
    if (selectedFiles[attachmentId]) {
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[attachmentId];
        return newFiles;
      });
    }
    setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  const handleCheckboxChange = (
    id: string,
    field: "sendToInspector" | "includeWithResults",
    checked: boolean
  ) => {
    setAttachments((prev) =>
      prev.map((att) => (att.id === id ? { ...att, [field]: checked } : att))
    );
  };

  const renderUploadArea = () => {
    const isDisabled = !formik.values.clientId || disabled;

    return (
      <Box
        sx={{
          height: 80,
          width: "calc(100% - 100px)",
          border: "1px dashed rgba(0, 0, 0, 0.12)",
          position: "relative",
          display: "flex",
          alignItems: "center",
          p: 0.8,
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          pointerEvents: isDisabled ? "none" : "auto",
          "&:hover": !isDisabled
            ? {
                borderColor: "#ff9800",
                backgroundColor: "#fff3e0",
              }
            : {},
        }}
        onClick={!isDisabled ? () => fileInputRef.current?.click() : undefined}
      >
        <UploadFileIcon
          sx={{ ml: 5 }}
          color={isDisabled ? "disabled" : "primary"}
        />
        <Box ml={2}>
          <Box sx={{ display: "flex" }}>
            <Typography
              color={isDisabled ? "text.disabled" : "primary"}
              sx={{ textDecoration: isDisabled ? "none" : "underline" }}
            >
              Click to upload
            </Typography>
            <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
          </Box>
          <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
            PDF, DOC, DOCX, XLSX, JPG or PNG (max. 10MB)
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderFileItem = (attachment: AttachmentFile) => {
    if (attachment.isUploaded) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #4caf50",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#F7FAF7",
            width: "calc(100% - 100px)",
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                {attachment.size !== "Unknown" ? `${attachment.size} • ` : ""}
                Complete
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
              disabled={disabled}
            >
              <DeleteOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else if (attachment.isUploading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #ff9800",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#fff3e0",
            width: "calc(100% - 100px)",
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                {attachment.size} • Uploading
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
            >
              <RemoveCircleOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #4caf50",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#F7FAF7",
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography>{attachment.size}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
            >
              <RemoveCircleOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box>
      <Typography variant="h5" className="section-label">
        Attachments
      </Typography>

      <Box className="field-layout">
        <Typography variant="h6" className="field-label-txt">
          Document(s)
        </Typography>
        <Box
          sx={{
            width: "calc(100% - 180px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                pb: 1,
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1, mr: 2, width: "494px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Document
                </Typography>
              </Box>
              <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Send to Inspector?
                </Typography>
              </Box>
              <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Include with Results?
                </Typography>
              </Box>
            </Box>

            {attachments.map((attachment) => (
              <Box
                key={attachment.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <Box sx={{ flex: 1, pr: 2, width: "494px !important" }}>
                  {renderFileItem(attachment)}
                </Box>
                <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                  <Checkbox
                    checked={attachment.sendToInspector}
                    onChange={(e) =>
                      handleCheckboxChange(
                        attachment.id,
                        "sendToInspector",
                        e.target.checked
                      )
                    }
                    size="small"
                    disabled={disabled}
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": { color: "primary.main" },
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                  <Checkbox
                    checked={attachment.includeWithResults}
                    onChange={(e) =>
                      handleCheckboxChange(
                        attachment.id,
                        "includeWithResults",
                        e.target.checked
                      )
                    }
                    size="small"
                    disabled={disabled}
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": { color: "primary.main" },
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1,
                mt: 1,
              }}
            >
              <Box sx={{ flex: 1, pr: 2, width: "494px !important" }}>
                {renderUploadArea()}
                {uploadError && (
                  <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                    {uploadErrorMessage}
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                <Box sx={{ height: "80px" }}></Box>
              </Box>
              <Box sx={{ minWidth: "140px", textAlign: "center", ml: 2 }}>
                <Box sx={{ height: "80px" }}></Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <input
        ref={fileInputRef}
        style={{
          display: "none",
        }}
        type="file"
        accept=".pdf,.doc,.docx,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/jpg,image/png"
        onChange={handleFileChange}
        onClick={(event) => {
          (event.target as HTMLInputElement).value = "";
        }}
      />
    </Box>
  );
};

export default Attachments;
